import { useEffect, useMemo, useState } from 'react';

import { ArrowLeft, Check, CircleAlert, Eye, LoaderCircle, Sparkles, Star } from 'lucide-react';

import { assetUrl, pptApi } from '../api';
import TemplatePreviewModal from './TemplatePreviewModal';

import styles from '../index.module.scss';

const ACADEMIC_CATEGORIES = new Set(['university', 'research']);

// 高校专区排在分类栏最前面，也是默认打开的分类
const PINNED_CATEGORY = 'university';

// 已选的模板不在高校专区时默认打开“全部”，免得回到这一步看不到已选项
function defaultFilter(catalog, pinned, selectedId) {
    if (catalog.loading) return null;
    if (!pinned) return 'all';
    const selected = catalog.templates.find((template) => template.id === selectedId);
    return selected && selected.category !== pinned.id ? 'all' : pinned.id;
}

function CategoryChip({ category, active, onSelect }) {
    return (
        <button
            type="button"
            className={styles.filterChip}
            aria-pressed={active}
            title={category.description}
            onClick={() => onSelect(category.id)}
        >
            {category.name}
            <span>{category.templateCount}</span>
        </button>
    );
}

function useCatalog() {
    const [state, setState] = useState({
        loading: true,
        error: false,
        templates: [],
        categories: [],
    });
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        let active = true;
        setState((current) => ({ ...current, loading: true, error: false }));
        Promise.all([pptApi.templates(), pptApi.categories()])
            .then(([templateData, categoryData]) => {
                if (!active) return;
                setState({
                    loading: false,
                    error: false,
                    templates: templateData?.templates || [],
                    categories: Array.isArray(categoryData) ? categoryData : [],
                });
            })
            .catch(() => {
                if (active) setState((current) => ({ ...current, loading: false, error: true }));
            });
        return () => {
            active = false;
        };
    }, [attempt]);

    return { ...state, reload: () => setAttempt((value) => value + 1) };
}

export default function TemplateStep({
    headingRef,
    scenario,
    selectedId,
    onSelect,
    onBack,
    canGenerate,
    generateHint,
    onGenerate,
}) {
    const catalog = useCatalog();
    const [chosenFilter, setFilter] = useState(null);
    const [preview, setPreview] = useState(null);
    const academic = scenario !== 'general';

    const categories = catalog.categories.filter((category) => category.templateCount > 0);
    const pinned = categories.find((category) => category.id === PINNED_CATEGORY);
    const others = categories.filter((category) => category.id !== PINNED_CATEGORY);
    const filter = chosenFilter ?? defaultFilter(catalog, pinned, selectedId);

    const visible = useMemo(() => {
        const list = catalog.templates.filter((template) => {
            if (filter === 'all') return true;
            if (filter === 'featured') return template.featured;
            return template.category === filter;
        });
        if (!academic || filter !== 'all') return list;
        return [...list].sort(
            (a, b) =>
                Number(ACADEMIC_CATEGORIES.has(b.category)) -
                Number(ACADEMIC_CATEGORIES.has(a.category)),
        );
    }, [catalog.templates, filter, academic]);

    return (
        <section className={styles.workspace} aria-labelledby="ppt-template-title">
            <div className={styles.panelHead}>
                <div>
                    <h2 id="ppt-template-title" ref={headingRef} tabIndex={-1}>
                        选择模板
                    </h2>
                    <p>
                        模板决定配色、版式与配图风格，内容仍按上一步的用途组织。点“预览”可以翻看每个模板的示例页。
                    </p>
                </div>
            </div>

            <div className={styles.filterBar} aria-label="模板分类">
                {pinned ? (
                    <CategoryChip
                        category={pinned}
                        active={filter === pinned.id}
                        onSelect={setFilter}
                    />
                ) : null}
                <button
                    type="button"
                    className={styles.filterChip}
                    aria-pressed={filter === 'all'}
                    onClick={() => setFilter('all')}
                >
                    全部<span>{catalog.templates.length}</span>
                </button>
                <button
                    type="button"
                    className={styles.filterChip}
                    aria-pressed={filter === 'featured'}
                    onClick={() => setFilter('featured')}
                >
                    <Star size={13} aria-hidden />
                    精品推荐
                </button>
                {others.map((category) => (
                    <CategoryChip
                        key={category.id}
                        category={category}
                        active={filter === category.id}
                        onSelect={setFilter}
                    />
                ))}
            </div>

            {catalog.loading ? (
                <div className={styles.loadingBlock} role="status">
                    <LoaderCircle className={styles.spin} size={18} aria-hidden />
                    正在加载模板…
                </div>
            ) : catalog.error ? (
                <div className={styles.notice} data-tone="error" role="alert">
                    <CircleAlert size={16} aria-hidden />
                    <p>模板加载失败，请检查网络后重试。</p>
                    <button type="button" className={styles.textButton} onClick={catalog.reload}>
                        重新加载
                    </button>
                </div>
            ) : visible.length === 0 ? (
                <p className={styles.emptyText}>这个分类下暂时没有模板，换个分类看看。</p>
            ) : (
                <ul className={styles.templateGrid}>
                    {visible.map((template) => {
                        const selected = template.id === selectedId;
                        const recommended = academic && ACADEMIC_CATEGORIES.has(template.category);
                        return (
                            <li key={template.id}>
                                <article className={styles.templateCard} data-selected={selected}>
                                    <button
                                        type="button"
                                        className={styles.templateCover}
                                        aria-pressed={selected}
                                        aria-label={`选择模板：${template.name}`}
                                        onClick={() => onSelect(template)}
                                    >
                                        <img
                                            src={assetUrl(template.coverUrl)}
                                            alt=""
                                            width={480}
                                            height={270}
                                            loading="lazy"
                                            decoding="async"
                                        />
                                        {selected ? (
                                            <span className={styles.selectedBadge}>
                                                <Check size={13} aria-hidden />
                                                已选择
                                            </span>
                                        ) : null}
                                    </button>
                                    <div className={styles.templateInfo}>
                                        <div className={styles.templateTitle}>
                                            <strong>{template.name}</strong>
                                            {recommended ? (
                                                <span className={styles.recommendTag}>
                                                    适合答辩
                                                </span>
                                            ) : template.featured ? (
                                                <span className={styles.featuredTag}>精品</span>
                                            ) : null}
                                        </div>
                                        <p>{template.description}</p>
                                        <div className={styles.templateMeta}>
                                            <span>
                                                {[
                                                    template.categoryName,
                                                    template.pageCount
                                                        ? `${template.pageCount} 页示例`
                                                        : null,
                                                ]
                                                    .filter(Boolean)
                                                    .join(' · ')}
                                            </span>
                                            <button
                                                type="button"
                                                className={styles.textButton}
                                                onClick={() => setPreview(template)}
                                            >
                                                <Eye size={14} aria-hidden />
                                                预览
                                            </button>
                                        </div>
                                    </div>
                                </article>
                            </li>
                        );
                    })}
                </ul>
            )}

            <div className={styles.foot}>
                <button type="button" className={styles.secondaryButton} onClick={onBack}>
                    <ArrowLeft size={16} aria-hidden />
                    上一步
                </button>
                <p className={styles.footNote}>{generateHint}</p>
                <div className={styles.footActions}>
                    <button
                        type="button"
                        className={styles.primaryButton}
                        disabled={!canGenerate}
                        onClick={onGenerate}
                    >
                        <Sparkles size={16} aria-hidden />
                        生成 PPT
                    </button>
                </div>
            </div>

            <TemplatePreviewModal
                template={preview}
                selected={preview?.id === selectedId}
                onClose={() => setPreview(null)}
                onUse={(template) => {
                    onSelect(template);
                    setPreview(null);
                }}
            />
        </section>
    );
}
